import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Overlay } from 'src/components/Overlay';
import AuthorDetailsTab from './AuthorDetailsTab';
import { ReviewDetailsProvider } from './ReviewDetailsContext';
import {
  getReviewRequestContextFromRoute,
  useReviewData
} from 'src/contexts/ReviewDataProvider';
import { useNavigate } from 'react-router-dom';
import { CoReviewInvitationLayout } from './CoReviewInvitationLayout';
import PastReviewsTab from './PastReviewTab';
import ReviewJournalHeader from './ReviewJournalHeader';
import InvitationPage from './InvitationPage';
import { ReviewTab } from './ReviewTab';
import ReviewSubmissionTab from './ReviewSubmissionTab';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { Box, Button, Flex, HStack, Tooltip } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import SingleItem from 'src/layouts/SingleItem';
import { faArrowLeft, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActivityTab from 'src/components/Submission/ActivityTab/ActivityTab';
import { buildFieldLabelMap } from 'src/components/Submission/ActivityTab/activityLogHandlers';
import NavbarDashboard from 'src/components/NavbarDashboard';
import {
  getPeerReviewModelForWorkflow,
  IRoleType
} from '@publishing-engine/domain/models';

export const REVIEW_DETAILS_TAB = {
  AUTHORS: 'Authors',
  REVIEW: 'Review',
  PAST_REVIEWS: 'Past Reviews',
  ACTIVITY: 'Activity'
} as const;
export type ReviewDetailsTab =
  (typeof REVIEW_DETAILS_TAB)[keyof typeof REVIEW_DETAILS_TAB];

export const reviewStatusMap: Record<string, string> = {
  approve: 'Publish',
  reject: 'Reject'
};

const renderTab = (
  tab: ReviewDetailsTab,
  fields: ISubmissionFieldsResponse,
  isDoubleAnonymous: boolean,
  options: {
    roleContext: IRoleType;
    submissionId?: string;
    submissionContextId?: string;
    reviewId?: string;
    fieldLabelMap?: Record<string, string>;
    enableConsolidatedPdf?: boolean;
  }
) => {
  const {
    roleContext,
    submissionId,
    submissionContextId,
    reviewId,
    fieldLabelMap,
    enableConsolidatedPdf
  } = options;
  switch (tab) {
    //   case REVIEW_DETAILS_TAB.SUBMISSION:
    //     return <SubmissionDetails />;
    case REVIEW_DETAILS_TAB.REVIEW:
      return (
        <ReviewSubmissionTab
          fields={fields}
          isDoubleAnonymous={isDoubleAnonymous}
          enableConsolidatedPdf={enableConsolidatedPdf}
        />
      );
    case REVIEW_DETAILS_TAB.AUTHORS:
      return <AuthorDetailsTab />;
    case REVIEW_DETAILS_TAB.PAST_REVIEWS:
      return <PastReviewsTab submissionContextId={submissionContextId} />;
    case REVIEW_DETAILS_TAB.ACTIVITY:
      return (
        <ActivityTab
          roleContext={roleContext}
          reviewId={reviewId}
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          fieldLabelMap={fieldLabelMap}
        />
      );
    default:
      return null;
  }
};

const ColumnScrollToTopButton = ({
  containerId,
  position
}: {
  containerId: string;
  position: 'left' | 'right';
}) => {
  const [showScroll, setShowScroll] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const scrollTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    scrollContainerRef.current = container;

    const checkScrollTop = () => setShowScroll(container.scrollTop > 300);
    container.addEventListener('scroll', checkScrollTop);
    return () => container.removeEventListener('scroll', checkScrollTop);
  }, [containerId]);

  if (!showScroll) return null;

  return (
    <Tooltip
      label="Scroll to top"
      aria-label="Scroll to top"
      hasArrow
      placement="left"
    >
      <Button
        onClick={scrollTop}
        position="fixed"
        bottom="95px"
        left={position === 'left' ? 'calc(50% - 60px)' : undefined}
        right={position === 'right' ? '24px' : undefined}
        zIndex={1000}
        size="sm"
        variant="primary"
        borderRadius="full"
        p={0}
        w="40px"
        h="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <FontAwesomeIcon icon={faArrowUp} size="lg" />
      </Button>
    </Tooltip>
  );
};

const Review = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = new URLSearchParams(location.search).get(
    'activeTab'
  ) as ReviewDetailsTab;
  const [activeTab, setActiveTab] = useState<ReviewDetailsTab>(
    defaultTab || REVIEW_DETAILS_TAB.REVIEW
  );
  const {
    review,
    reviewerOrCoReviewer,
    pastReviewsData,
    workflowResponse,
    submissionContext,
    fieldsResponse,
    submissionId
  } = useReviewData();
  const { fields } = fieldsResponse;
  const fieldLabelMap = useMemo(() => buildFieldLabelMap(fields), [fields]);
  const firstReview = pastReviewsData?.previousReviews?.[0];
  const submissionCurrentState = workflowResponse.workflowData.currentState;
  const { roleContext, reviewId } = getReviewRequestContextFromRoute();

  const isPastReviewsAvailable =
    firstReview &&
    firstReview?.rounds?.length > 0 &&
    (submissionCurrentState === 'revisionAssessment' ||
      submissionCurrentState === 'revisionPeerReviewInvitation' ||
      submissionCurrentState === 'revisionPeerReviewPending' ||
      submissionCurrentState === 'revisionPeerReviewFinished' ||
      submissionCurrentState === 'preProduction' ||
      submissionCurrentState === 'revision');

  const [reviewFormTrigger] = useState<boolean>(false);

  const closeItem = () => {
    const routeState =
      (location.state as { from?: string; parentFrom?: string } | null) ?? null;
    const from = routeState?.from;
    const parentFrom = routeState?.parentFrom;

    if (from) {
      return navigate(from, {
        state: parentFrom ? { from: parentFrom } : null
      });
    }

    if (location.key !== 'default') {
      return navigate(-1);
    }

    return navigate('/app/reviews?reviewListType=active');
  };

  const isDoubleAnonymous =
    getPeerReviewModelForWorkflow(
      submissionContext?.config,
      fieldsResponse?.articleWorkflowId
    ) === 'doubleAnonymous';

  const filteredTabs = Object.values(REVIEW_DETAILS_TAB).filter(tab => {
    if (tab === REVIEW_DETAILS_TAB.AUTHORS) {
      return false;
    }

    if (tab === REVIEW_DETAILS_TAB.PAST_REVIEWS && !isPastReviewsAvailable) {
      return false;
    }

    return true;
  });

  const activeTabIndex = Math.max(filteredTabs.indexOf(activeTab), 0);
  const activeTabValue =
    filteredTabs[activeTabIndex] ?? REVIEW_DETAILS_TAB.REVIEW;

  if (
    reviewerOrCoReviewer === 'coReviewer' &&
    review.caseData.coReview?.status === 'pending'
  ) {
    return <CoReviewInvitationLayout onClose={closeItem} />;
  }

  return (
    <ReviewDetailsProvider activeTab={activeTab} setActiveTab={setActiveTab}>
      <Overlay
        id="scrollable-wrapper"
        // `clip`, not `hidden`: both clip identically, but `hidden` still makes
        // the box a scroll container, so the form's scrollToFirstError would
        // scroll *this* box (sliding the navbar and both columns up and leaving
        // the modal backdrop showing at the bottom) instead of only scrolling
        // the column that holds the field. `clip` is not scrollable, so the
        // error scroll stops at the column.
        overflow="clip"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        <NavbarDashboard submissionContextId={submissionContext?.id} />
        {['invited', 'revisionInvited'].includes(
          review.workflow.currentState
        ) ? (
          <Box flex="1" minHeight="0" overflowY="auto">
            <Box p={2} mb={0} bg="white">
              <HStack>
                <Box>
                  <ButtonText onClick={closeItem}>
                    <FontAwesomeIcon icon={faArrowLeft} size="lg" /> Back
                  </ButtonText>
                </Box>
              </HStack>
            </Box>

            <InvitationPage />
          </Box>
        ) : (
          <>
            <Flex
              flex="1"
              minHeight="0"
              width="100%"
              bg="white"
              // Same reason as the wrapper above — keep this row out of the
              // scrollIntoView ancestor chain.
              overflow="clip"
              flexDirection={{ base: 'column', xl: 'row' }}
              paddingBottom="90px"
            >
              <Box
                width={{ base: '100%', xl: '50%' }}
                minWidth={0}
                backgroundColor="white"
                overflow="auto"
                height={{ base: '50%', xl: '100%' }}
                paddingBottom="90px"
                id="review-left-column"
              >
                <ReviewJournalHeader onClose={closeItem} />
                <SingleItem.Navigation size="compact">
                  {filteredTabs.map(tab => (
                    <SingleItem.Tab
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      isActive={tab === activeTabValue}
                    >
                      {tab}
                    </SingleItem.Tab>
                  ))}
                </SingleItem.Navigation>
                <Box p={6}>
                  {renderTab(activeTabValue, fields, isDoubleAnonymous, {
                    roleContext,
                    reviewId,
                    submissionId: submissionId,
                    submissionContextId: submissionContext.id,
                    fieldLabelMap,
                    enableConsolidatedPdf:
                      submissionContext?.config?.enableConsolidatedPdf
                  })}
                </Box>
              </Box>
              <Flex
                width={{ base: '100%', xl: '50%' }}
                minWidth={0}
                bg="surfaceSecondaryFill"
                overflow="auto"
                height={{ base: '50%', xl: '100%' }}
                paddingBottom="90px"
                id="review-right-column"
              >
                <ReviewTab reviewFormTrigger={reviewFormTrigger} />
              </Flex>
            </Flex>

            <ColumnScrollToTopButton
              containerId="review-left-column"
              position="left"
            />
            <ColumnScrollToTopButton
              containerId="review-right-column"
              position="right"
            />
          </>
        )}
        <Box id="common-footer-root" />
      </Overlay>
    </ReviewDetailsProvider>
  );
};

export default Review;
